(() => {
  'use strict';
  function calculate(quantity, roseUnit, wrapUnit) {
    const count = Math.max(1, Math.floor(Number(quantity) || 1));
    return { quantity: count, wrapping: count * wrapUnit, total: count * (roseUnit + wrapUnit) };
  }
  function assembleRoseGift(fields, wrapId, message, title, group) {
    const values = new Map(fields);
    const id = Number(values.get('id'));
    const quantity = Number(values.get('quantity') || 1);
    if (![id, Number(wrapId), quantity].every(value => Number.isSafeInteger(value) && value > 0) || id === Number(wrapId)) throw new Error('Invalid rose selection');
    const properties = {};
    for (const [name, value] of fields) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim()) properties[match[1]] = String(value);
    }
    delete properties['Gift message'];
    const card = String(message || '').trim().slice(0, 500);
    Object.assign(properties, { _nl_rose_gift: group, _nl_rose_role: 'rose' });
    if (card) properties['Gift message'] = card;
    const rose = { id, quantity, properties };
    if (values.get('selling_plan')) rose.selling_plan = values.get('selling_plan');
    const wrapping = { id: Number(wrapId), quantity, parent_id: id, properties: { _nl_rose_gift: group, _nl_rose_role: 'wrapping', 'For rose': title } };
    if (card) wrapping.properties['Gift message'] = card;
    return [rose, wrapping];
  }
  function groupUpdates(items, group, quantity) {
    const roses = items.filter(item => item.properties?._nl_rose_gift === group && item.properties?._nl_rose_role === 'rose');
    const wraps = items.filter(item => item.properties?._nl_rose_gift === group && item.properties?._nl_rose_role === 'wrapping');
    if (!roses.length || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Invalid wrapping group');
    // Discounts can split one rose selection into paid and free cart lines.
    // Consolidate onto the native parent, retaining the wrapping relationship.
    const parent = roses.find(rose => wraps.some(wrap => wrap.parent_relationship?.parent_key === rose.key)) || roses[0];
    const updates = Object.fromEntries([...roses, ...wraps].map(item => [item.key, 0]));
    updates[parent.key] = quantity;
    if (wraps.length) updates[wraps[0].key] = quantity;
    return updates;
  }
  if (typeof module !== 'undefined') module.exports = { calculate, assembleRoseGift, groupUpdates };
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.NewlandsRoseGifts) { window.NewlandsRoseGifts.init(document); return; }
  function request(url, data) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(data ? 'POST' : 'GET', url);
      xhr.setRequestHeader('Accept', 'application/json');
      if (data) xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = 25000;
      xhr.onload = () => {
        let result;
        try { result = JSON.parse(xhr.responseText); } catch { reject(new Error('Invalid response')); return; }
        if (xhr.status < 200 || xhr.status >= 300) { reject(new Error(result.description || result.message || 'Request failed')); return; }
        resolve(result);
      };
      xhr.onerror = xhr.ontimeout = xhr.onabort = () => reject(new Error('Connection interrupted'));
      xhr.send(data ? JSON.stringify(data) : null);
    });
  }
  const rootPath = value => (value || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  function setup(panel) {
    if (panel.dataset.wrapBound) return;
    const form = panel.closest('form');
    if (!form) return;
    panel.dataset.wrapBound = 'true';
    const toggle = panel.querySelector('[data-wrap-toggle]');
    const details = panel.querySelector('[data-wrap-details]');
    const message = panel.querySelector('[data-wrap-message]');
    const status = panel.querySelector('[data-wrap-status]');
    const basket = panel.querySelector('[data-wrap-basket]');
    const previousMessage = form.querySelector('.nl-rose-gift-message');
    const payment = form.querySelector('.shopify-payment-button');
    const button = form.querySelector('[data-product-atc]');
    const selection = form.querySelector('variant-selection');
    const text = JSON.parse(panel.querySelector('[data-wrap-text]').textContent);
    const variants = JSON.parse(panel.querySelector('[data-wrap-variants]').textContent);
    const money = new Intl.NumberFormat('en-IE', { style: 'currency', currency: panel.dataset.currency || 'EUR' });
    let busy = false;
    const currentId = () => selection?.getAttribute('variant') || form.querySelector('[name="id"]')?.value;
    function update() {
      const unitPrice = variants[currentId()] ?? Number(Object.values(variants)[0]) ?? 0;
      const totals = calculate(form.querySelector('[name="quantity"]')?.value, Number(unitPrice), Number(panel.dataset.wrapUnit));
      details.hidden = !toggle.checked;
      toggle.setAttribute('aria-expanded', String(toggle.checked));
      panel.querySelector('[data-wrap-action]').textContent = toggle.checked ? text.added : text.add;
      panel.querySelector('[data-wrap-calculation]').textContent = text.calculation.replace('__QUANTITY__', totals.quantity).replace('__PRICE__', money.format(Number(panel.dataset.wrapUnit) / 100));
      panel.querySelector('[data-wrap-total]').textContent = money.format(totals.wrapping / 100);
      panel.querySelector('[data-wrap-order-total]').textContent = money.format(totals.total / 100);
      panel.querySelector('[data-wrap-multiple]').hidden = totals.quantity < 2;
      panel.querySelector('[data-wrap-count]').textContent = message.value.length + ' / 500';
      if (previousMessage) previousMessage.hidden = toggle.checked;
      if (payment) payment.hidden = toggle.checked;
      if (!toggle.checked && !busy) { status.hidden = true; status.textContent = ''; basket.hidden = true; }
    }
    panel.nlRoseSubmit = async event => {
      if (!toggle.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy || button?.disabled || !form.reportValidity()) return;
      const group = crypto.randomUUID();
      const fields = [...new FormData(form).entries(), ['id', currentId()]];
      let items;
      try { items = assembleRoseGift(fields, panel.dataset.wrapVariant, message.value, panel.dataset.productTitle, group); }
      catch { status.hidden = false; status.textContent = text.error; return; }
      busy = true;
      const wasDisabled = !!button?.disabled;
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); button.classList.add('processing'); }
      toggle.disabled = true;
      status.hidden = false;
      status.textContent = text.adding;
      basket.hidden = true;
      const root = rootPath(panel.dataset.root);
      try {
        await request(root + 'cart/add.js', { items });
        window.location.assign(root + 'cart');
      } catch {
        try {
          const cart = await request(root + 'cart.js');
          const added = cart.items.filter(item => item.properties?._nl_rose_gift === group);
          if (added.length === 2 && added.every(item => item.quantity === items[0].quantity)) { window.location.assign(root + 'cart'); return; }
        } catch {}
        status.textContent = text.error;
        basket.hidden = false;
        busy = false;
        toggle.disabled = false;
        if (button) { button.disabled = wasDisabled; button.removeAttribute('aria-busy'); button.classList.remove('processing'); }
      }
    };
    form.addEventListener('input', update);
    form.addEventListener('change', update);
    form.addEventListener('click', () => requestAnimationFrame(update));
    if (selection) new MutationObserver(update).observe(selection, { attributes: true, attributeFilter: ['variant'] });
    update();
    panel.hidden = false;
  }
  function init(scope) { scope.querySelectorAll('[data-rose-wrap]').forEach(setup); }
  window.addEventListener('submit', event => {
    const panel = event.target.querySelector?.('[data-rose-wrap]');
    panel?.nlRoseSubmit?.(event);
  }, true);
  let cartBusy = false;
  let cartTimer;
  let pendingChange;
  const cartConfig = document.querySelector('[data-rose-wrap-cart-text]');
  if (cartConfig) {
    const text = JSON.parse(cartConfig.textContent);
    const root = rootPath(text.root);
    const status = document.querySelector('[data-rose-wrap-cart-status]');
    const checkoutButtons = () => [...document.querySelectorAll('form[action$="/cart"] [name="checkout"]')];
    const groupRows = group => [...document.querySelectorAll('[data-rose-gift-group]')].filter(row => row.dataset.roseGiftGroup === group);
    const hasWrap = row => groupRows(row.dataset.roseGiftGroup).some(item => item.dataset.roseGiftRole === 'wrapping');
    const stop = event => { event.preventDefault(); event.stopImmediatePropagation(); };
    const blockCheckout = blocked => { checkoutButtons().forEach(button => { button.disabled = blocked; }); document.querySelectorAll('.additional-checkout-buttons').forEach(box => { box.hidden = blocked; }); };
    async function updateCart(row, quantity, remove) {
      if (cartBusy) return;
      cartBusy = true;
      pendingChange = null;
      clearTimeout(cartTimer);
      blockCheckout(true);
      status.hidden = false;
      status.textContent = text.updating;
      document.querySelectorAll('[data-cartitem] input,[data-cartitem] button').forEach(input => { input.disabled = true; });
      try {
        const cart = await request(root + 'cart.js');
        const group = row.dataset.roseGiftGroup;
        const note = document.querySelector('form[action$="/cart"] [name="note"]')?.value ?? cart.note;
        if (remove) {
          const items = cart.items.filter(item => item.properties?._nl_rose_gift === group && (row.dataset.roseGiftRole === 'rose' || item.properties?._nl_rose_role === 'wrapping'));
          await request(root + 'cart/update.js', { updates: Object.fromEntries(items.map(item => [item.key, 0])), note });
        } else {
          await request(root + 'cart/update.js', { updates: groupUpdates(cart.items, group, quantity), note });
        }
        window.location.reload();
      } catch {
        status.textContent = text.error;
        const link = document.createElement('a');
        link.href = root + 'cart';
        link.textContent = ' ' + text.reload;
        status.append(link);
      }
    }
    window.addEventListener('click', event => {
      const row = event.target.closest?.('[data-rose-gift-group]');
      if (!row) return;
      const removal = event.target.closest('[data-cartitem-remove]');
      if (removal) { stop(event); updateCart(row, 0, true); return; }
      const control = event.target.closest('[data-quantity-plus],[data-quantity-minus]');
      if (!control || row.dataset.roseGiftRole !== 'rose') return;
      stop(event);
      const input = row.querySelector('[data-quantity-input]');
      const increment = Number(input.dataset.incrementValue) || 1;
      const quantity = Math.max(1, Number(input.value) + (control.hasAttribute('data-quantity-plus') ? increment : -increment));
      input.value = quantity;
      updateCart(row, quantity, false);
    }, true);
    function onQuantity(event) {
      const input = event.target.closest?.('[data-quantity-input]');
      const row = input?.closest('[data-rose-gift-role="rose"]');
      if (!row) return;
      stop(event);
      if (cartBusy) return;
      clearTimeout(cartTimer);
      const quantity = Number(input.value);
      if (!Number.isSafeInteger(quantity) || quantity < 1) { pendingChange = null; blockCheckout(true); input.reportValidity(); return; }
      blockCheckout(true);
      pendingChange = () => updateCart(row, quantity, false);
      cartTimer = setTimeout(pendingChange, 400);
    }
    window.addEventListener('input', onQuantity, true);
    window.addEventListener('change', onQuantity, true);
    window.addEventListener('submit', event => {
      if (cartBusy || pendingChange) { stop(event); if (!cartBusy) pendingChange(); }
    }, true);
  }
  window.NewlandsRoseGifts = { init, calculate, assembleRoseGift, groupUpdates };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
